import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, ProtectedRoute } from "./lib/auth";
import { FeaturesProvider } from "./lib/features";
import AppShell from "./components/app/AppShell";
import ComingSoon from "./components/app/ComingSoon";
import AdminOnly from "./components/app/AdminOnly";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import AuthCallback from "./pages/AuthCallback";

import Dashboard from "./pages/Dashboard";
import Imports from "./pages/Imports";
import Customers from "./pages/Customers";
import Products from "./pages/Products";
import Opportunities from "./pages/Opportunities";
import CRM from "./pages/CRM";
import DataQuality from "./pages/DataQuality";
import BusinessIntelligence from "./pages/BusinessIntelligence";
import AICommandCenter from "./pages/AICommandCenter";
import Analytics from "./pages/Analytics";
import Admin from "./pages/Admin";

import SettingsLayout from "./pages/settings/SettingsLayout";
import ProfileSettings from "./pages/settings/ProfileSettings";
import OrganizationSettings from "./pages/settings/OrganizationSettings";
import SecuritySettings from "./pages/settings/SecuritySettings";
import TeamSettings from "./pages/settings/TeamSettings";

import NotFound from "./pages/NotFound";
import Unauthorized from "./pages/Unauthorized";

// Every core module is ALWAYS routable. Feature gating happens INSIDE the
// page as a polished empty state — never at the router / navigation level.
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Protected — always visible, no capability gating at the route level */}
      <Route path="/dashboard"               element={<ProtectedRoute><AppShell><Dashboard /></AppShell></ProtectedRoute>} />
      <Route path="/imports"                 element={<ProtectedRoute><AppShell><Imports /></AppShell></ProtectedRoute>} />
      <Route path="/customers"               element={<ProtectedRoute><AppShell><Customers /></AppShell></ProtectedRoute>} />
      <Route path="/products"                element={<ProtectedRoute><AppShell><Products /></AppShell></ProtectedRoute>} />
      <Route path="/data-quality"            element={<ProtectedRoute><AppShell><DataQuality /></AppShell></ProtectedRoute>} />
      <Route path="/business-intelligence"   element={<ProtectedRoute><AppShell><BusinessIntelligence /></AppShell></ProtectedRoute>} />
      <Route path="/analytics"               element={<ProtectedRoute><AppShell><Analytics /></AppShell></ProtectedRoute>} />
      <Route path="/opportunities"           element={<ProtectedRoute><AppShell><Opportunities /></AppShell></ProtectedRoute>} />
      <Route path="/revenue-opportunities"   element={<Navigate to="/opportunities" replace />} />
      <Route path="/ai-command-center"       element={<ProtectedRoute><AppShell><AICommandCenter /></AppShell></ProtectedRoute>} />
      <Route path="/crm"                     element={<ProtectedRoute><AppShell><CRM /></AppShell></ProtectedRoute>} />
      <Route path="/campaigns"               element={<ProtectedRoute><AppShell>
        <ComingSoon testid="cs-campaigns" title="Campaigns" description="Trigger campaigns from real cohorts — never blast lists again." />
      </AppShell></ProtectedRoute>} />
      <Route path="/automation"              element={<ProtectedRoute><AppShell>
        <ComingSoon testid="cs-automation" title="Automation" description="Recipes that automate revenue actions across your stack." />
      </AppShell></ProtectedRoute>} />

      {/* Admin — only visible & accessible for admin@milkath.com */}
      <Route path="/admin" element={<ProtectedRoute><AppShell><AdminOnly><Admin /></AdminOnly></AppShell></ProtectedRoute>} />

      {/* Settings */}
      <Route path="/settings" element={<ProtectedRoute><AppShell><SettingsLayout /></AppShell></ProtectedRoute>}>
        <Route index element={<Navigate to="profile" replace />} />
        <Route path="profile" element={<ProfileSettings />} />
        <Route path="organization" element={<OrganizationSettings />} />
        <Route path="security" element={<SecuritySettings />} />
        <Route path="team" element={<TeamSettings />} />
        <Route path="billing" element={<ComingSoon testid="cs-billing" title="Billing" description="Plan, payment method, and invoice history." />} />
        <Route path="notifications" element={<ComingSoon testid="cs-notifications" title="Notifications" description="Choose what gets surfaced via in-app, email or webhooks." />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <FeaturesProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster theme="dark" position="bottom-right" toastOptions={{
            style: { background: "#18181b", border: "1px solid #27272a", color: "#fafafa" },
          }} />
        </BrowserRouter>
      </FeaturesProvider>
    </AuthProvider>
  );
}
